/**
 * Pure helpers for New Members (the leader tray and the admin desk).
 * No Firestore, no React: easy to test.
 */
import type { OutreachActivity, OutreachConfig, OutreachRecord } from '@/types/outreach';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "SL" for "Sarah Levi"; one letter for one word; "?" when empty. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = [...words[0]][0] ?? '';
  const last = words.length > 1 ? ([...words[words.length - 1]][0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Stable pastel-ish avatar colour from the member id (same person, same colour). */
const AVATAR_COLORS = ['#7e57c2', '#2a9d8f', '#c0462a', '#3b82a0', '#a0527c', '#6b8e23', '#b8860b'];
export function avatarColor(id: string): string {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

/** "just now", "2 hours ago", "yesterday", "3 days ago", "3 weeks ago". Worked out in the app, no timers. */
export function agePhrase(thenMs: number, nowMs: number): string {
  const diff = Math.max(0, nowMs - thenMs);
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) {
    const m = Math.floor(diff / MINUTE);
    return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diff < DAY) {
    const h = Math.floor(diff / HOUR);
    return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
  }
  const d = Math.floor(diff / DAY);
  if (d === 1) return 'yesterday';
  if (d < 14) return `${d} days ago`;
  return `${Math.floor(d / 7)} weeks ago`;
}

export const joinedLabel = (joinedAtMs: number, nowMs: number) =>
  `Joined ${agePhrase(joinedAtMs, nowMs)}`;

export type WaitTone = 'none' | 'gentle' | 'needsHand';
export interface WaitBadge {
  tone: WaitTone;
  label: string;
}

/**
 * Gentle labels, never alarms. Uses the same thresholds the backend will use for
 * reminders (24h and 72h by default) so the words match later notifications.
 */
export function waitBadge(
  queuedAtMs: number,
  nowMs: number,
  thresholds: Pick<OutreachConfig, 'reminderAfterHours' | 'escalateAfterHours'> = {
    reminderAfterHours: 24,
    escalateAfterHours: 72,
  }
): WaitBadge {
  const hours = (nowMs - queuedAtMs) / HOUR;
  if (hours >= thresholds.escalateAfterHours)
    return { tone: 'needsHand', label: 'Could use a hand' };
  if (hours >= thresholds.reminderAfterHours) return { tone: 'gentle', label: 'Waiting a day' };
  return { tone: 'none', label: '' };
}

export function firstNameOf(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] || 'there';
}

/** Fill {{firstName}} {{hebrewName}} {{leaderName}}. Unknown variables are left as written. */
export function fillTemplate(
  body: string,
  vars: { firstName: string; hebrewName?: string; leaderName: string }
): string {
  const map: Record<string, string> = {
    firstName: vars.firstName,
    hebrewName: vars.hebrewName ?? vars.firstName,
    leaderName: vars.leaderName,
  };
  return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (whole, key: string) => map[key] ?? whole);
}

/**
 * Join the template text and the leader's own line into one message.
 * Whitespace is trimmed; the personal line goes after a blank line.
 */
export function composeHello(templateText: string, personalLine: string): string {
  const base = templateText.trim();
  const own = personalLine.trim();
  return own ? `${base}\n\n${own}` : base;
}

export interface PersonalLineState {
  ok: boolean;
  /** Characters still needed (0 when ok). */
  remaining: number;
  count: number;
}

export function personalLineState(
  line: string,
  rule: { required: boolean; minChars: number }
): PersonalLineState {
  const count = line.trim().length;
  if (!rule.required) return { ok: true, remaining: 0, count };
  const remaining = Math.max(0, rule.minChars - count);
  return { ok: remaining === 0, remaining, count };
}

/** A claim that is still live and held by someone else. */
export function claimHeldByOther(
  record: Pick<OutreachRecord, 'claim'>,
  myId: string,
  nowMs: number
): { name: string } | null {
  const claim = record.claim;
  if (!claim) return null;
  if (claim.leaderId === myId) return null;
  if (claim.expiresAt.toMillis() <= nowMs) return null;
  return { name: claim.leaderName };
}

/** Oldest first: whoever has waited longest is at the top. Falls back to joinedAt. */
export function sortWaiting<T extends Pick<OutreachRecord, 'queuedAt' | 'joinedAt'>>(
  records: T[]
): T[] {
  const ms = (r: T) => (r.queuedAt ?? r.joinedAt)?.toMillis?.() ?? 0;
  return [...records].sort((a, b) => ms(a) - ms(b));
}

/** True when the leader still has to accept the current acknowledgement. */
export function needsAcknowledgement(
  profile: { outreachAckVersion?: number } | null,
  config: Pick<OutreachConfig, 'acknowledgement'>
): boolean {
  return (profile?.outreachAckVersion ?? 0) < config.acknowledgement.version;
}

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  apple: 'Apple',
  facebook: 'Facebook',
  email: 'Email',
};

/** "Google", "Email"... for how the member signed up. Unknown providers are shown as written. */
export function providerLabel(provider: string | undefined): string {
  if (!provider) return '';
  return PROVIDER_LABELS[provider] ?? (provider === 'password' ? 'Email' : provider);
}

/** Admin-editable text: lines starting with "- " are promises; other lines are the intro. */
export function parseAcknowledgement(text: string): { intro: string; promises: string[] } {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const promises = lines.filter((l) => l.startsWith('- ')).map((l) => l.slice(2).trim());
  const intro = lines.filter((l) => !l.startsWith('- ')).join(' ');
  return { intro, promises };
}

/** "45 min", "3 hours", "2 days". For stat tiles. */
export function durationLabel(ms: number): string {
  const diff = Math.max(0, ms);
  if (diff < HOUR) {
    const m = Math.max(1, Math.round(diff / MINUTE));
    return `${m} min`;
  }
  if (diff < DAY) {
    const h = Math.round(diff / HOUR);
    return `${h} ${h === 1 ? 'hour' : 'hours'}`;
  }
  const d = Math.round(diff / DAY);
  return `${d} ${d === 1 ? 'day' : 'days'}`;
}

const msOf = (t: { toMillis?: () => number } | undefined): number | undefined => t?.toMillis?.();

export interface OutreachStats {
  waiting: number;
  /** Time the longest-waiting member has waited, or null when nobody waits. */
  longestWaitMs: number | null;
  welcomedThisWeek: number;
  /** Median time from joining (or being re-added) to the first hello, last 30 days. */
  typicalTimeToHelloMs: number | null;
}

/** The four stat tiles on the admin list, worked out from the loaded records. */
export function outreachStats(
  waiting: OutreachRecord[],
  welcomed: OutreachRecord[],
  nowMs: number
): OutreachStats {
  const since = (r: OutreachRecord) => msOf(r.queuedAt) ?? msOf(r.joinedAt) ?? nowMs;
  const longestWaitMs = waiting.length ? nowMs - Math.min(...waiting.map(since)) : null;

  const helloAt = (r: OutreachRecord) => msOf(r.firstContactedAt) ?? msOf(r.stageChangedAt);
  const week = nowMs - 7 * DAY;
  const month = nowMs - 30 * DAY;
  const times: number[] = [];
  let welcomedThisWeek = 0;
  for (const r of welcomed) {
    const at = helloAt(r);
    if (at === undefined) continue;
    if (at >= week) welcomedThisWeek += 1;
    if (at >= month) times.push(Math.max(0, at - since(r)));
  }
  times.sort((a, b) => a - b);
  const mid = Math.floor(times.length / 2);
  let typicalTimeToHelloMs: number | null = null;
  if (times.length) {
    typicalTimeToHelloMs = times.length % 2 ? times[mid] : (times[mid - 1] + times[mid]) / 2;
  }
  return { waiting: waiting.length, longestWaitMs, welcomedThisWeek, typicalTimeToHelloMs };
}

/** Plain-words timeline entry. Never alarming. */
export function activityText(event: Pick<OutreachActivity, 'type' | 'actorName' | 'meta'>): string {
  const who = event.actorName || 'A leader';
  switch (event.type) {
    case 'joined':
      return 'Joined the community';
    case 'notified':
      return 'Leaders were told';
    case 'claimed':
      return `${who} started writing`;
    case 'claim_released':
      return `${who} stopped writing`;
    case 'first_contacted':
      return `${who} said hello`;
    case 'member_replied':
      return 'They replied';
    case 'closed':
      return `${who} closed this`;
    case 'reopened':
      return `${who} reopened this`;
    case 'requeued': {
      const reason = event.meta?.reason ? `: ${String(event.meta.reason)}` : '';
      return `${who} added them to New Members${reason}`;
    }
    default:
      return 'Updated';
  }
}

// ---------------------------------------------------------------------------
// Quiet mode wording
// ---------------------------------------------------------------------------

function quietStamp(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(ms));
}

/** "Fri 6:12 PM to Sat 7:12 PM", in the community's time zone. */
export function quietRangeLabel(startMs: number, endMs: number, timeZone: string): string {
  return `${quietStamp(startMs, timeZone)} to ${quietStamp(endMs, timeZone)}`;
}

/** "Fri Oct 9", in the community's time zone. */
export function quietDayLabel(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(ms));
}

/** "Starts in 3 hours" / "Starts in 2 days" / "Ends in 45 minutes". */
export function quietCountdown(targetMs: number, nowMs: number, verb: 'Starts' | 'Ends'): string {
  const diff = Math.max(0, targetMs - nowMs);
  const minutes = Math.round(diff / 60_000);
  if (minutes < 60) return `${verb} in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(diff / 3_600_000);
  if (hours < 36) return `${verb} in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(diff / 86_400_000);
  return `${verb} in ${days} days`;
}

// ---------------------------------------------------------------------------
// Leader pause ("need a break?")
// ---------------------------------------------------------------------------

export type PauseChoice = 'hour' | 'tomorrow' | 'week';

/** When a pause ends: an hour from now, tomorrow at 9 AM (device time), or a week from now. */
export function pauseUntilMs(choice: PauseChoice, nowMs: number): number {
  if (choice === 'hour') return nowMs + HOUR;
  if (choice === 'week') return nowMs + 7 * DAY;
  const d = new Date(nowMs);
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.getTime();
}

/** "Wed 9:00 AM", in the device's time zone. */
export function pauseEndLabel(untilMs: number): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(untilMs));
}

/**
 * Email to leaders about new members is Phase 2, so leaders are not offered the switch at
 * launch. The backend and the switch are built and tested; flip this to true with the email
 * setup in docs/new-member-outreach/08-email-setup.md.
 */
export const OUTREACH_EMAIL_AVAILABLE = false;
